// styled.ts — estilos + animaciones + constantes del onboarding de Novah.
// Qué hace: define todos los styled-components, keyframes, constantes de preview
//   (PREVIEW_SAMPLE_IMAGES, SAMPLE_DYNAMIC_TEXTS) y el helper isValidUrl.
// Qué exporta: todos los styled-components + constantes + isValidUrl.
// Quién lo usa: CreateWorkspaceModal, ChatBubble y los componentes de steps/.

import styled, { keyframes } from 'styled-components';

// ─── Animations ──────────────────────────────────────────────────────────────

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

// ─── Styled Components ───────────────────────────────────────────────────────

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 55vh;
  overflow-y: auto;
  padding: 4px 2px;
  scroll-behavior: smooth;
  max-width: 100%;
  min-width: 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 2px; }
`;

export const MsgRow = styled.div<{ $from: 'novah' | 'user' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  justify-content: ${({ $from }) => $from === 'user' ? 'flex-end' : 'flex-start'};
  animation: ${fadeIn} 0.3s ease-out;
`;

export const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.25);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

export const Bubble = styled.div<{ $from: 'novah' | 'user' }>`
  max-width: 85%;
  padding: 8px 12px;
  border-radius: ${({ $from }) => $from === 'novah' ? '4px 12px 12px 12px' : '12px 4px 12px 12px'};
  font-size: 13px;
  line-height: 1.5;
  background: ${({ $from, theme }) => $from === 'novah' ? theme.colors.background : theme.colors.primary};
  color: ${({ $from, theme }) => $from === 'novah' ? theme.colors.textSecondary : '#fff'};
  border: ${({ $from, theme }) => $from === 'novah' ? theme.borders.thin : 'none'};
  strong { color: ${({ $from, theme }) => $from === 'novah' ? theme.colors.text : '#fff'}; }
`;

export const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 10px 14px;
  span {
    width: 6px; height: 6px; border-radius: 50%;
    background: ${({ theme }) => theme.colors.textMuted};
    animation: ${dotBounce} 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

export const InputArea = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 12px;
  margin-top: 8px;
  max-width: 100%;
  overflow: hidden;
  min-width: 0;
`;

export const SourceChoiceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SourceChoiceBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: rgba(59, 130, 246, 0.04);
  }
`;

export const SourceIcon = styled.span`
  font-size: 18px;
  width: 24px;
  text-align: center;
`;

export const SourceLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

export const SourceDesc = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const UrlRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const UrlInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
`;

export const ImgGrid = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  margin: 8px 0;
  max-width: 100%;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }
`;

export const ImgItem = styled.div<{ $selected?: boolean }>`
  position: relative;
  flex: 0 0 80px;
  width: 80px;
  height: 80px;
  border: 3px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.15s ease;
  box-shadow: ${({ $selected }) => $selected ? '0 0 0 2px rgba(59,130,246,0.3)' : 'none'};
  scroll-snap-align: start;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.05);
  }
  img { width: 100%; height: 100%; object-fit: contain; padding: 4px; }
`;

export const Badge = styled.div`
  position: absolute; top: 2px; right: 2px;
  width: 16px; height: 16px; border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff; font-size: 9px;
  display: flex; align-items: center; justify-content: center;
`;

export const TplGrid = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 8px 2px;
  margin: 8px 0;
  max-width: 100%;
  scroll-snap-type: x mandatory;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 2px; }
`;

export const TplCard = styled.div<{ $selected?: boolean }>`
  min-width: 130px;
  max-width: 130px;
  flex-shrink: 0;
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  transition: all 0.15s ease;
  opacity: ${({ $selected }) => $selected ? 1 : 0.5};
  scroll-snap-align: start;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; opacity: 1; }
  position: relative;
`;

export const TplPreview = styled.div`
  aspect-ratio: 9/16;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; background: #f3f4f6;
  canvas { width: 100%; height: 100%; object-fit: contain; }
`;

export const TplName = styled.div`
  padding: 5px 6px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TplActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 11px;
`;

export const SelectAllLink = styled.button`
  background: none; border: none; padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px; font-weight: 600; cursor: pointer;
  &:hover { text-decoration: underline; }
`;

export const MixCheckboxGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 8px 0;
`;

export const MixCheckItem = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid ${({ $checked, theme }) => $checked ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $checked }) => $checked ? 'rgba(59,130,246,0.04)' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

export const MixCheckbox = styled.input`
  accent-color: ${({ theme }) => theme.colors.primary};
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

export const MixLabel = styled.span`
  font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.text};
`;

export const MixHint = styled.span`
  font-size: 11px; color: ${({ theme }) => theme.colors.textMuted};
`;

export const MixSummary = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-top: 6px;
  padding: 6px;
  background: rgba(16,185,129,0.06);
  border-radius: 6px;
`;

export const HiddenInput = styled.input`display: none;`;

export const FontPreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0;
`;

export const FontPreviewItem = styled.div<{ $selected?: boolean }>`
  padding: 12px;
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  background: ${({ $selected }) => $selected ? 'rgba(59,130,246,0.06)' : 'transparent'};
  transition: all 0.15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

export const FontName = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

export const FontSample = styled.div`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.3;
`;

export const BackBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

export const SuccessBox = styled.div`
  text-align: center;
  padding: 12px 0;
  animation: ${fadeIn} 0.3s ease-out;
`;

// ─── Constantes de preview ───────────────────────────────────────────────────

// Sample backgrounds for template previews (stock-like images via Pexels)
export const PREVIEW_SAMPLE_IMAGES = [
  'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400',
  'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=400',
  'https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg?auto=compress&cs=tinysrgb&w=400',
  'https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=400',
];

export const SAMPLE_DYNAMIC_TEXTS: Record<string, string> = {
  headline: 'Your Brand',
  tagline: 'Tagline goes here',
  cta: 'Learn More',
  quote: 'Quality content that connects',
};

// Valida que el texto parezca una URL/dominio (con o sin scheme). Evita mandar
// al scraper cosas que claramente no son una web y da feedback claro al usuario.
export function isValidUrl(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i.test(v);
}
